import React from 'react';
import styled, { keyframes } from 'styled-components';

const DirectSellingFeatures = () => {
  const features = [
    {
      id: 1,
      icon: '🚀',
      title: 'Team Building Systems',
      description: 'Our proven recruitment frameworks help you build depth and width in your organization with personalized strategies that work for your specific company and products.',
    },
    {
      id: 2,
      icon: '🔄',
      title: 'Duplication Strategy',
      description: 'Create scalable systems that your entire team can follow, increasing retention and producing reliable growth month after month.',
    },
    {
      id: 3,
      icon: '📱',
      title: 'Social Media Mastery',
      description: 'Learn how to leverage platforms like Instagram, TikTok, and Facebook to attract high-quality prospects without being "that" person in the DMs.',
    },
    {
      id: 4,
      icon: '🤖',
      title: 'Automation Technology',
      description: 'Implement custom automation tools that nurture leads, follow up with prospects, and free up your time for high-value activities.',
    },
    {
      id: 5,
      icon: '📊',
      title: 'Performance Analytics',
      description: 'Get real-time insights into your team\'s performance metrics, helping you identify growth opportunities and address challenges proactively.',
    },
    {
      id: 6,
      icon: '🎯',
      title: 'Leadership Development',
      description: 'Transform from salesperson to leader with our executive coaching program designed specifically for direct selling professionals.',
    },
  ];

  return (
    <FeaturesContainer id="features">
      <SectionHeader>
        <FeatureBadge>OUR SERVICES</FeatureBadge>
        <SectionTitle>Direct Selling <HighlightText>SUCCESS STRATEGIES</HighlightText></SectionTitle>
        <SectionDescription>
          We provide customized solutions to help you build, grow, and <strong>scale your direct selling business</strong> with proven systems that drive results.
        </SectionDescription>
      </SectionHeader>
      
      <FeaturesGrid>
        {features.map((feature) => (
          <FeatureCard key={feature.id}>
            <FeatureIcon>{feature.icon}</FeatureIcon>
            <FeatureTitle>{feature.title}</FeatureTitle>
            <FeatureDescription>{feature.description}</FeatureDescription>
          </FeatureCard>
        ))}
      </FeaturesGrid>
      
      <ActionCTA>
        <CTAText>Ready to take your direct selling business to the next level?</CTAText>
        <CTAButton href="#contact">Schedule Your Strategy Session</CTAButton>
      </ActionCTA>
      
      <ComparisonSection>
        <ComparisonTitle>How We Compare to Typical "Success Coaches"</ComparisonTitle>
        <ComparisonTable>
          <thead>
            <tr>
              <th>Features</th>
              <th>Our Strategic Approach</th>
              <th>Generic Coaches</th>
              <th>DIY Method</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Customized Strategy</td>
              <td>✓ Tailored to your business</td>
              <td>× One-size-fits-all</td>
              <td>× Trial and error</td>
            </tr>
            <tr>
              <td>Proven Results</td>
              <td>✓ 1,500+ success stories</td>
              <td>? Vague case studies</td>
              <td>× Inconsistent</td>
            </tr>
            <tr>
              <td>Industry Expertise</td>
              <td>✓ Direct selling specialists</td>
              <td>× General business coaches</td>
              <td>× Limited knowledge</td>
            </tr>
            <tr>
              <td>Technology Integration</td>
              <td>✓ Advanced automation tools</td>
              <td>× Basic suggestions</td>
              <td>× None or basic</td>
            </tr>
            <tr>
              <td>ROI Timeframe</td>
              <td>✓ 30-90 days</td>
              <td>× 6+ months</td>
              <td>× Unpredictable</td>
            </tr>
          </tbody>
        </ComparisonTable>
      </ComparisonSection>
    </FeaturesContainer>
  );
};

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(20px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const FeaturesContainer = styled.section`
  padding: 100px 30px;
  background-color: #fff;
  
  @media (max-width: 991px) {
    padding: 80px 30px;
  }
  
  @media (max-width: 768px) {
    padding: 60px 20px;
  }
`;

const SectionHeader = styled.div`
  text-align: center;
  max-width: 800px;
  margin: 0 auto 60px;
  animation: ${fadeIn} 0.8s ease-out;
  
  @media (max-width: 768px) {
    margin-bottom: 40px;
  }
`;

const FeatureBadge = styled.div`
  display: inline-block;
  background: rgba(213, 111, 234, 0.1);
  color: #9C27B0;
  font-weight: 600;
  font-size: 0.9rem;
  padding: 8px 16px;
  border-radius: 50px;
  margin-bottom: 15px;
  letter-spacing: 1.5px;
`;

const SectionTitle = styled.h2`
  font-size: 2.8rem;
  font-weight: 800;
  color: #333;
  margin-bottom: 20px;
  line-height: 1.2;
  
  @media (max-width: 991px) {
    font-size: 2.4rem;
  }
  
  @media (max-width: 768px) {
    font-size: 2rem;
  }
`;

const HighlightText = styled.span`
  color: #9C27B0;
  position: relative;
`;

const SectionDescription = styled.p`
  font-size: 1.2rem;
  color: #666;
  line-height: 1.6;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
  }
`;

const FeaturesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 30px;
  margin-bottom: 60px;
  animation: ${fadeIn} 1s ease-out;
  
  @media (max-width: 991px) {
    grid-template-columns: repeat(2, 1fr);
  }
  
  @media (max-width: 650px) {
    grid-template-columns: 1fr;
  }
`;

const FeatureCard = styled.div`
  background: white;
  border-radius: 12px;
  padding: 35px 30px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.05);
  border: 1px solid #f0f0f0;
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 35px rgba(0, 0, 0, 0.1);
    border-color: rgba(213, 111, 234, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 30px 25px;
  }
`;

const FeatureIcon = styled.div`
  font-size: 2.5rem;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 2.2rem;
  }
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 700;
  color: #333;
  margin-bottom: 15px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const FeatureDescription = styled.p`
  font-size: 1rem;
  color: #666;
  line-height: 1.6;
`;

const ActionCTA = styled.div`
  text-align: center;
  margin-bottom: 80px;
  animation: ${fadeIn} 1.2s ease-out;
  
  @media (max-width: 768px) {
    margin-bottom: 60px;
  }
`;

const CTAText = styled.p`
  font-size: 1.3rem;
  font-weight: 600;
  color: #333;
  margin-bottom: 20px;
  
  @media (max-width: 768px) {
    font-size: 1.2rem;
  }
`;

const CTAButton = styled.a`
  display: inline-block;
  background: linear-gradient(135deg, #D56FEA 0%, #9C27B0 100%);
  color: white;
  font-weight: 600;
  font-size: 1.1rem;
  padding: 15px 35px;
  border-radius: 8px;
  text-decoration: none;
  transition: all 0.3s ease;
  box-shadow: 0 8px 20px rgba(213, 111, 234, 0.3);
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 12px 25px rgba(213, 111, 234, 0.4);
  }
  
  @media (max-width: 768px) {
    font-size: 1rem;
    padding: 14px 30px;
  }
`;

const ComparisonSection = styled.div`
  max-width: 1000px;
  margin: 0 auto;
  animation: ${fadeIn} 1.4s ease-out;
`;

const ComparisonTitle = styled.h3`
  font-size: 1.8rem;
  font-weight: 700;
  color: #333;
  text-align: center;
  margin-bottom: 30px;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 25px;
  }
`;

const ComparisonTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  box-shadow: 0 5px 20px rgba(0, 0, 0, 0.05);
  border-radius: 10px;
  overflow: hidden;
  
  th, td {
    padding: 15px 20px;
    text-align: center;
    border-bottom: 1px solid #f0f0f0;
    
    @media (max-width: 768px) {
      padding: 12px 15px;
      font-size: 0.9rem;
    }
    
    @media (max-width: 580px) {
      padding: 10px;
      font-size: 0.8rem;
    }
  }
  
  th {
    background: linear-gradient(135deg, rgba(213, 111, 234, 0.1) 0%, rgba(156, 39, 176, 0.15) 100%);
    font-weight: 700;
    color: #333;
    font-size: 1.1rem;
    
    @media (max-width: 768px) {
      font-size: 1rem;
    }
    
    @media (max-width: 580px) {
      font-size: 0.9rem;
    }
  }
  
  th:first-child {
    text-align: left;
  }
  
  td:first-child {
    text-align: left;
    font-weight: 600;
    color: #333;
  }
  
  tbody tr:nth-child(even) {
    background-color: #f9f9f9;
  }
  
  tbody tr:last-child td {
    border-bottom: none;
  }
  
  @media (max-width: 480px) {
    display: block;
    overflow-x: auto;
    white-space: nowrap;
  }
`;

export default DirectSellingFeatures; 